package floris.graphics.backoffice.pages.admin.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.materialdesignsystem.components.widgets.DsEditableField
import com.materialdesignsystem.components.widgets.FilledButton
import com.materialdesignsystem.components.widgets.FilledTonalButton
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.core.constants.Res
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.attributes.AutoComplete
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

@Page("/admin/settings")
@Composable
fun SettingsPage() {
    val ctx = rememberPageContext()

    val viewModel = remember { SettingsViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collectLatest {
            when (it) {
                is SettingsUiEvent.Navigate -> ctx.router.navigateTo(it.route)
                is SettingsUiEvent.ShowError -> TODO()
            }
        }
    }

    SettingsPageContent(state = state, onEvent = viewModel::onEvent)
}

@Composable
fun SettingsPageContent(
    state: SettingsState,
    onEvent: (SettingsEvent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout("Settings") {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = if (breakpoint >= Breakpoint.MD) 58.px else 36.px),
            horizontalAlignment = Alignment.Start
        ) {
            Column(
                modifier = Modifier.fillMaxSize(),
                verticalArrangement = Arrangement.spacedBy(24.px),
                horizontalAlignment = Alignment.Start
            ) {
                H1(
                    attrs = Modifier
                        .fillMaxWidth()
                        .borderBottom(1.px, LineStyle.Solid, Color.gray)
                        .toAttrs(),
                ) { Text("Profiel") }

                DsEditableField(
                    modifier = Modifier.fillMaxWidth(if (breakpoint == Breakpoint.XL) 40.percent else 100.percent),
                    id = "FullName",
                    label = Res.String.FullName,
                    placeholder = Res.String.EnterName,
                    value = state.fullName ?: "",
                    onValueChange = { onEvent(SettingsEvent.UpdateFullName(it)) },
                    autoComplete = AutoComplete.name
                )

                DsEditableField(
                    modifier = Modifier.fillMaxWidth(if (breakpoint == Breakpoint.XL) 40.percent else 100.percent),
                    id = "Email",
                    label = Res.String.Email,
                    placeholder = Res.String.EnterName,
                    value = state.userName,
                    onValueChange = { onEvent(SettingsEvent.UpdateEmail(it)) },
                    autoComplete = AutoComplete.email
                )

                FilledButton(
                    modifier = Modifier.margin(top = 16.px),
                    enabled = state.hasChanged,
                    onClick = { onEvent(SettingsEvent.Update) }
                ) {
                    Text(Res.String.Update)
                }
            }

            Column(
                modifier = Modifier.fillMaxWidth().margin(top = 68.px),
                verticalArrangement = Arrangement.spacedBy(24.px)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth()
                        .borderBottom(1.px, LineStyle.Solid, Color.gray),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    H1 { Text("Wachtwoord") }

                    FilledTonalButton(
                        onClick = { onEvent(SettingsEvent.ToggleChangingPassword) }
                    ) {
                        Text(if (state.isChangingPassword) Res.String.Hide else Res.String.ChangePassword)
                    }
                }

                if (!state.isChangingPassword) {
                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint == Breakpoint.XL) 40.percent else 100.percent),
                        label = "Wachtwoord",
                        type = InputType.Password,
                        readOnly = true,
                        value = "                              ",
                        onValueChange = {},
                        placeholder = "",
                        id = "password_placeholder"
                    )
                } else {
                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint == Breakpoint.XL) 40.percent else 100.percent),
                        label = "Oud wachtwoord",
                        type = InputType.Password,
                        value = state.oldPassword,
                        onValueChange = { onEvent(SettingsEvent.ChangeOldPassword(it)) },
                        placeholder = "",
                        id = "old_password",
                        autoComplete = AutoComplete.currentPassword
                    )

                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint == Breakpoint.XL) 40.percent else 100.percent),
                        label = "Nieuw wachtwoord",
                        type = InputType.Password,
                        value = state.newPassword,
                        onValueChange = { onEvent(SettingsEvent.ChangeNewPassword(it)) },
                        placeholder = "",
                        id = "new_password",
                        autoComplete = AutoComplete.newPassword
                    )

                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint == Breakpoint.XL) 40.percent else 100.percent),
                        label = "Bevestig nieuw wachtwoord",
                        type = InputType.Password,
                        value = state.confirmNewPassword,
                        onValueChange = { onEvent(SettingsEvent.ChangeConfirmNewPassword(it)) },
                        placeholder = "",
                        id = "confirm_new_password",
                        autoComplete = AutoComplete.newPassword
                    )

                    FilledButton(
                        onClick = { onEvent(SettingsEvent.UpdatePassword) },
                        enabled = state.validNewPassword
                    ) {
                        Text(Res.String.UpdatePassword)
                    }
                }
            }
        }
    }
}
